/**
 * OnboardingDataPanel
 *
 * Displays the client's onboarding data in the editor sidebar.
 * Contact info, address, and social links are editable inline.
 * Other sections (brand profile, industry, style) remain read-only reference.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronDown,
  ChevronRight,
  User,
  Palette,
  MapPin,
  MessageSquare,
  Sparkles,
  Pencil,
  Check,
  X,
  Loader2,
  Upload,
  Trash2,
  Image,
  Clock,
} from 'lucide-react';
import { updateBusinessInfo, uploadLogo, removeLogo } from '../../api/editor';
import { apiGet } from '../../api/client';
import type { BusinessInfoUpdate } from '../../api/editor';
import { LogoCropper } from '../../components/onboarding/LogoCropper';

// =============================================================================
// TYPES
// =============================================================================

interface OnboardingDataResponse {
  success: boolean;
  hasOnboardingData: boolean;
  message?: string;
  summary?: OnboardingSummary;
}

interface OnboardingSummary {
  industryCategory: string | null;
  industryType: string | null;
  industrySpecific: Record<string, unknown> | null;
  themeMode: string | null;
  tone: string | null;
  priorities: string[];
  primaryColor: string | null;
  secondaryColor: string | null;
  hasLogo: boolean;
  logoUrl: string | null;
  logoHeightClass?: string;
  logoHeightOptions?: readonly string[];
  businessDescription: string | null;
  tagline: string | null;
  services: string | null;
  yearsInBusiness: number | null;
  whatMakesUnique: string | null;
  hasPhysicalLocation: boolean;
  serviceAreaLabel?: string | null;
  address?: string | null;
  city: string | null;
  state: string | null;
  zipCode?: string | null;
  phone: string | null;
  email: string | null;
  showEmailOnWebsite?: boolean;
  domainPreference: string | null;
  hours?: Record<string, { open: string; close: string; closed?: boolean }> | null;
  social?: {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    youtube?: string;
    yelp?: string;
    google?: string;
  };
  brandProfile: {
    coreSummary: string | null;
    atmosphere: string | null;
    visualStyle: string | null;
    tone: string | null;
    idealClient: string | null;
    uniqueValue: string | null;
    services: string[];
    imageKeywords: string[];
    footerDescription: string | null;
  } | null;
  completedAt: string | null;
}

interface OnboardingDataPanelProps {
  websiteId: string;
  onBusinessInfoChanged?: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function OnboardingDataPanel({ websiteId, onBusinessInfoChanged }: OnboardingDataPanelProps) {
  const [data, setData] = useState<OnboardingDataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const json = await apiGet<OnboardingDataResponse>(
          `/api/editor/${websiteId}/onboarding-data`
        );
        setData(json);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [websiteId]);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  // Handle saving business info changes
  const handleSaveBusinessInfo = useCallback(
    async (updates: BusinessInfoUpdate): Promise<boolean> => {
      try {
        const result = await updateBusinessInfo(websiteId, updates);
        if (result.success) {
          // Update local state to reflect changes
          if (data?.summary) {
            const s = { ...data.summary };
            if (updates.address !== undefined) s.address = updates.address;
            if (updates.city !== undefined) s.city = updates.city;
            if (updates.state !== undefined) s.state = updates.state;
            if (updates.zipCode !== undefined) s.zipCode = updates.zipCode;
            if (updates.hasPhysicalLocation !== undefined) s.hasPhysicalLocation = updates.hasPhysicalLocation;
            if (updates.serviceAreaLabel !== undefined) s.serviceAreaLabel = updates.serviceAreaLabel;
            if (updates.phone !== undefined) s.phone = updates.phone;
            if (updates.email !== undefined) s.email = updates.email;
            if (updates.showEmailOnWebsite !== undefined) s.showEmailOnWebsite = updates.showEmailOnWebsite;
            if (updates.hours !== undefined) s.hours = updates.hours;
            if (updates.facebook !== undefined || updates.instagram !== undefined ||
                updates.linkedin !== undefined || updates.youtube !== undefined ||
                updates.yelp !== undefined || updates.googleBusiness !== undefined) {
              s.social = { ...s.social };
              if (updates.facebook !== undefined) s.social.facebook = updates.facebook;
              if (updates.instagram !== undefined) s.social.instagram = updates.instagram;
              if (updates.linkedin !== undefined) s.social.linkedin = updates.linkedin;
              if (updates.youtube !== undefined) s.social.youtube = updates.youtube;
              if (updates.yelp !== undefined) s.social.yelp = updates.yelp;
              if (updates.googleBusiness !== undefined) s.social.google = updates.googleBusiness;
            }
            setData({ ...data, summary: s });
          }
          onBusinessInfoChanged?.();
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },
    [websiteId, data, onBusinessInfoChanged]
  );

  if (loading) {
    return (
      <div className="p-4 text-center text-gray-500 text-sm">
        Loading client data...
      </div>
    );
  }

  if (error || !data?.hasOnboardingData) {
    return (
      <div className="p-4 text-center text-gray-500 text-sm">
        {error || 'No onboarding data available'}
      </div>
    );
  }

  const { summary } = data;
  if (!summary) return null;

  const formatIndustry = (category: string | null, type: string | null) => {
    if (!category) return 'Not specified';
    const cat = category.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const typ = type?.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return typ ? `${cat} → ${typ}` : cat;
  };

  const formatTone = (tone: string | null) => {
    if (!tone) return 'Not specified';
    return tone.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div className="h-full overflow-y-auto bg-white border-l border-gray-200">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 z-10">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <User className="w-4 h-4" />
          Client Info
        </h3>
        <p className="text-xs text-gray-500 mt-0.5">Onboarding data &amp; contact info</p>
      </div>

      <div className="p-3 space-y-2">
        {/* AI Brand Profile (read-only) */}
        {summary.brandProfile && (
          <CollapsibleSection
            title="AI Brand Profile"
            icon={<Sparkles className="w-4 h-4 text-purple-500" />}
            isExpanded={expandedSections.has('brand-profile')}
            onToggle={() => toggleSection('brand-profile')}
          >
            <div className="space-y-3 text-sm">
              {summary.brandProfile.coreSummary && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Summary</label>
                  <p className="text-gray-700 mt-1">{summary.brandProfile.coreSummary}</p>
                </div>
              )}
              {summary.brandProfile.uniqueValue && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Unique Value</label>
                  <p className="text-gray-700 mt-1">{summary.brandProfile.uniqueValue}</p>
                </div>
              )}
              {summary.brandProfile.idealClient && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ideal Client</label>
                  <p className="text-gray-700 mt-1">{summary.brandProfile.idealClient}</p>
                </div>
              )}
              {summary.brandProfile.atmosphere && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Atmosphere</label>
                  <p className="text-gray-700 mt-1">{summary.brandProfile.atmosphere}</p>
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Business Details (read-only) */}
        <CollapsibleSection
          title="Business"
          icon={<MessageSquare className="w-4 h-4 text-blue-500" />}
          isExpanded={expandedSections.has('business')}
          onToggle={() => toggleSection('business')}
        >
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Industry</label>
              <p className="text-gray-700 mt-1">{formatIndustry(summary.industryCategory, summary.industryType)}</p>
            </div>
            {summary.businessDescription && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Their Description</label>
                <p className="text-gray-700 mt-1 italic">"{summary.businessDescription}"</p>
              </div>
            )}
            {summary.whatMakesUnique && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">What Makes Them Unique</label>
                <p className="text-gray-700 mt-1 italic">"{summary.whatMakesUnique}"</p>
              </div>
            )}
            {summary.tagline && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tagline</label>
                <p className="text-gray-700 mt-1">"{summary.tagline}"</p>
              </div>
            )}
            {summary.services && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Services</label>
                <p className="text-gray-700 mt-1 whitespace-pre-line">{summary.services}</p>
              </div>
            )}
            {summary.yearsInBusiness && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Years in Business</label>
                <p className="text-gray-700 mt-1">{summary.yearsInBusiness}</p>
              </div>
            )}
          </div>
        </CollapsibleSection>

        {/* Brand & Style (read-only) */}
        <CollapsibleSection
          title="Brand & Style"
          icon={<Palette className="w-4 h-4 text-pink-500" />}
          isExpanded={expandedSections.has('brand')}
          onToggle={() => toggleSection('brand')}
        >
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Primary</label>
                <div className="flex items-center gap-2 mt-1">
                  <div
                    className="w-6 h-6 rounded border border-gray-300"
                    style={{ backgroundColor: summary.primaryColor || '#ccc' }}
                  />
                  <span className="text-gray-700 font-mono text-xs">{summary.primaryColor || '—'}</span>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Secondary</label>
                <div className="flex items-center gap-2 mt-1">
                  <div
                    className="w-6 h-6 rounded border border-gray-300"
                    style={{ backgroundColor: summary.secondaryColor || '#ccc' }}
                  />
                  <span className="text-gray-700 font-mono text-xs">{summary.secondaryColor || '—'}</span>
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Theme</label>
              <p className="text-gray-700 mt-1 capitalize">{summary.themeMode || 'Not specified'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tone</label>
              <p className="text-gray-700 mt-1">{formatTone(summary.tone)}</p>
            </div>
            {summary.priorities && summary.priorities.length > 0 && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Priorities</label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {summary.priorities.map((p, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full capitalize"
                    >
                      {p.replace(/-/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <LogoEditor
              websiteId={websiteId}
              hasLogo={summary.hasLogo}
              logoUrl={summary.logoUrl}
              logoHeightClass={summary.logoHeightClass}
              logoHeightOptions={summary.logoHeightOptions}
              onLogoHeightChanged={(heightClass) => {
                if (data?.summary) {
                  setData({
                    ...data,
                    summary: { ...data.summary, logoHeightClass: heightClass },
                  });
                }
                // Scaffold was rebuilt server-side — refresh the preview.
                onBusinessInfoChanged?.();
              }}
              onLogoChanged={(newUrl) => {
                // Update local state so UI reflects immediately
                if (data?.summary) {
                  setData({
                    ...data,
                    summary: {
                      ...data.summary,
                      hasLogo: !!newUrl,
                      logoUrl: newUrl,
                    },
                  });
                }
                onBusinessInfoChanged?.();
              }}
            />
          </div>
        </CollapsibleSection>

        {/* Location & Contact (EDITABLE) */}
        <CollapsibleSection
          title="Location & Contact"
          icon={<MapPin className="w-4 h-4 text-green-500" />}
          isExpanded={expandedSections.has('location')}
          onToggle={() => toggleSection('location')}
          badge="Editable"
        >
          <EditableContactFields
            summary={summary}
            onSave={handleSaveBusinessInfo}
          />
        </CollapsibleSection>
      </div>
    </div>
  );
}

// =============================================================================
// EDITABLE CONTACT FIELDS
// =============================================================================

interface EditableContactFieldsProps {
  summary: OnboardingSummary;
  onSave: (updates: BusinessInfoUpdate) => Promise<boolean>;
}

function EditableContactFields({ summary, onSave }: EditableContactFieldsProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Editable field values
  const [hasPhysicalLocation, setHasPhysicalLocation] = useState(summary.hasPhysicalLocation);
  const [serviceAreaLabel, setServiceAreaLabel] = useState(summary.serviceAreaLabel || '');
  const [address, setAddress] = useState(summary.address || '');
  const [city, setCity] = useState(summary.city || '');
  const [state, setState] = useState(summary.state || '');
  const [zipCode, setZipCode] = useState(summary.zipCode || '');
  const [phone, setPhone] = useState(summary.phone || '');
  const [email, setEmail] = useState(summary.email || '');
  const [showEmailOnWebsite, setShowEmailOnWebsite] = useState(summary.showEmailOnWebsite !== false);
  const [facebook, setFacebook] = useState(summary.social?.facebook || '');
  const [instagram, setInstagram] = useState(summary.social?.instagram || '');
  const [linkedin, setLinkedin] = useState(summary.social?.linkedin || '');
  const [youtube, setYoutube] = useState(summary.social?.youtube || '');
  const [yelp, setYelp] = useState(summary.social?.yelp || '');
  const [google, setGoogle] = useState(summary.social?.google || '');
  const [hours, setHours] = useState<HoursData>(summary.hours || DEFAULT_HOURS);

  // Sync local state when summary changes from parent
  useEffect(() => {
    setHasPhysicalLocation(summary.hasPhysicalLocation);
    setServiceAreaLabel(summary.serviceAreaLabel || '');
    setAddress(summary.address || '');
    setCity(summary.city || '');
    setState(summary.state || '');
    setZipCode(summary.zipCode || '');
    setPhone(summary.phone || '');
    setEmail(summary.email || '');
    setShowEmailOnWebsite(summary.showEmailOnWebsite !== false);
    setFacebook(summary.social?.facebook || '');
    setInstagram(summary.social?.instagram || '');
    setLinkedin(summary.social?.linkedin || '');
    setYoutube(summary.social?.youtube || '');
    setYelp(summary.social?.yelp || '');
    setGoogle(summary.social?.google || '');
    setHours(summary.hours || DEFAULT_HOURS);
  }, [summary]);

  const handleCancel = () => {
    // Reset to current values
    setHasPhysicalLocation(summary.hasPhysicalLocation);
    setServiceAreaLabel(summary.serviceAreaLabel || '');
    setAddress(summary.address || '');
    setCity(summary.city || '');
    setState(summary.state || '');
    setZipCode(summary.zipCode || '');
    setPhone(summary.phone || '');
    setEmail(summary.email || '');
    setShowEmailOnWebsite(summary.showEmailOnWebsite !== false);
    setFacebook(summary.social?.facebook || '');
    setInstagram(summary.social?.instagram || '');
    setLinkedin(summary.social?.linkedin || '');
    setYoutube(summary.social?.youtube || '');
    setYelp(summary.social?.yelp || '');
    setGoogle(summary.social?.google || '');
    setHours(summary.hours || DEFAULT_HOURS);
    setEditing(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);

    // Build updates object with only changed fields
    const updates: BusinessInfoUpdate = {};
    if (hasPhysicalLocation !== summary.hasPhysicalLocation) updates.hasPhysicalLocation = hasPhysicalLocation;
    if (serviceAreaLabel !== (summary.serviceAreaLabel || '')) updates.serviceAreaLabel = serviceAreaLabel;
    if (address !== (summary.address || '')) updates.address = address;
    if (city !== (summary.city || '')) updates.city = city;
    if (state !== (summary.state || '')) updates.state = state;
    if (zipCode !== (summary.zipCode || '')) updates.zipCode = zipCode;
    if (phone !== (summary.phone || '')) updates.phone = phone;
    if (email !== (summary.email || '')) updates.email = email;
    if (showEmailOnWebsite !== (summary.showEmailOnWebsite !== false)) updates.showEmailOnWebsite = showEmailOnWebsite;
    if (facebook !== (summary.social?.facebook || '')) updates.facebook = facebook;
    if (instagram !== (summary.social?.instagram || '')) updates.instagram = instagram;
    if (linkedin !== (summary.social?.linkedin || '')) updates.linkedin = linkedin;
    if (youtube !== (summary.social?.youtube || '')) updates.youtube = youtube;
    if (yelp !== (summary.social?.yelp || '')) updates.yelp = yelp;
    if (google !== (summary.social?.google || '')) updates.googleBusiness = google;

    // Check if hours changed
    const origHours = summary.hours || DEFAULT_HOURS;
    const hoursChanged = JSON.stringify(hours) !== JSON.stringify(origHours);
    if (hoursChanged) updates.hours = hours;

    if (Object.keys(updates).length === 0) {
      setEditing(false);
      setSaving(false);
      return;
    }

    const success = await onSave(updates);
    setSaving(false);

    if (success) {
      setEditing(false);
    } else {
      setSaveError('Failed to save. Please try again.');
    }
  };

  if (!editing) {
    return (
      <div className="space-y-3 text-sm">
        {/* Display mode */}
        <div className="flex justify-end">
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded transition-colors"
          >
            <Pencil className="w-3 h-3" />
            Edit
          </button>
        </div>

        {/* Location type indicator */}
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Location Type</label>
          <p className="text-gray-700 mt-1 flex items-center gap-1.5">
            <span className={`inline-block w-2 h-2 rounded-full ${summary.hasPhysicalLocation ? 'bg-green-400' : 'bg-amber-400'}`} />
            {summary.hasPhysicalLocation ? 'Physical Location' : 'Service Area (No Storefront)'}
          </p>
        </div>

        {summary.hasPhysicalLocation && (
          <>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Address</label>
              <p className="text-gray-700 mt-1">
                {summary.address || '—'}
              </p>
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">City</label>
                <p className="text-gray-700 mt-1">{summary.city || '—'}</p>
              </div>
              <div className="w-16">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">State</label>
                <p className="text-gray-700 mt-1">{summary.state || '—'}</p>
              </div>
              <div className="w-20">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Zip</label>
                <p className="text-gray-700 mt-1">{summary.zipCode || '—'}</p>
              </div>
            </div>
          </>
        )}

        {!summary.hasPhysicalLocation && (summary.city || summary.state) && (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Service Area</label>
            <p className="text-gray-700 mt-1">
              Serving {summary.serviceAreaLabel || [summary.city, summary.state].filter(Boolean).join(', ') || '—'}
            </p>
          </div>
        )}

        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Phone</label>
          <p className="text-gray-700 mt-1">{summary.phone || '—'}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</label>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-gray-700">{summary.email || '—'}</p>
            {summary.email && (
              <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                summary.showEmailOnWebsite !== false
                  ? 'text-green-700 bg-green-50'
                  : 'text-gray-500 bg-gray-100'
              }`}>
                {summary.showEmailOnWebsite !== false ? 'Visible on site' : 'Hidden on site'}
              </span>
            )}
          </div>
        </div>

        {/* Social links */}
        {(summary.social?.facebook || summary.social?.instagram || summary.social?.linkedin ||
          summary.social?.youtube || summary.social?.yelp || summary.social?.google) && (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Social</label>
            <div className="mt-1 space-y-1">
              {summary.social?.facebook && (
                <SocialDisplay label="Facebook" url={summary.social.facebook} />
              )}
              {summary.social?.instagram && (
                <SocialDisplay label="Instagram" url={summary.social.instagram} />
              )}
              {summary.social?.linkedin && (
                <SocialDisplay label="LinkedIn" url={summary.social.linkedin} />
              )}
              {summary.social?.youtube && (
                <SocialDisplay label="YouTube" url={summary.social.youtube} />
              )}
              {summary.social?.yelp && (
                <SocialDisplay label="Yelp" url={summary.social.yelp} />
              )}
              {summary.social?.google && (
                <SocialDisplay label="Google" url={summary.social.google} />
              )}
            </div>
          </div>
        )}

        {/* Hours summary */}
        {summary.hours && (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Hours
            </label>
            <p className="text-gray-700 mt-1 text-xs">
              {formatHoursCompact(summary.hours)}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Edit mode
  return (
    <div className="space-y-3 text-sm">
      {/* Edit mode header */}
      <div className="flex justify-end gap-2">
        <button
          onClick={handleCancel}
          disabled={saving}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors"
        >
          <X className="w-3 h-3" />
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1 px-2 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Check className="w-3 h-3" />
          )}
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {saveError && (
        <div className="px-2 py-1.5 bg-red-50 text-red-600 text-xs rounded">
          {saveError}
        </div>
      )}

      {/* Physical location toggle */}
      <div className="flex items-center justify-between py-1">
        <label className="text-xs font-medium text-gray-700">Physical location?</label>
        <button
          type="button"
          onClick={() => setHasPhysicalLocation(!hasPhysicalLocation)}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
            hasPhysicalLocation ? 'bg-blue-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
              hasPhysicalLocation ? 'translate-x-4' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>
      <p className="text-[11px] text-gray-400 -mt-2">
        {hasPhysicalLocation
          ? 'Full address will appear on the website'
          : 'Service area business — no storefront address shown'}
      </p>

      {/* Address fields (only when physical location) */}
      {hasPhysicalLocation && (
        <>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Address</label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Street address"
              className="mt-1 w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
                className="mt-1 w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="w-16">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">State</label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="ST"
                maxLength={2}
                className="mt-1 w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="w-20">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Zip</label>
              <input
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                placeholder="00000"
                maxLength={10}
                className="mt-1 w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </>
      )}

      {/* City/State only for service area businesses */}
      {!hasPhysicalLocation && (
        <>
        {/*
          Wording shown in the pre-header and footer. City/state below still
          drive the map link and structured data, so both are kept — this only
          overrides the displayed phrase, which "<city>, <state>" cannot express
          for a regional or nationwide business.
        */}
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Service area wording
          </label>
          <div className="flex items-center gap-1 mt-1">
            <span className="text-sm text-gray-400 shrink-0">Serving</span>
            <input
              type="text"
              value={serviceAreaLabel}
              onChange={(e) => setServiceAreaLabel(e.target.value)}
              placeholder={[city, state].filter(Boolean).join(', ') || 'City, ST'}
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            Leave blank to use the city and state below. Appears in the pre-header and footer.
          </p>
        </div>

        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Service Area (City)</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="City"
              className="mt-1 w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="w-16">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">State</label>
            <input
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="ST"
              maxLength={2}
              className="mt-1 w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        </>
      )}

      {/* Phone */}
      <div>
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Phone</label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="(555) 123-4567"
          className="mt-1 w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Email with visibility toggle */}
      <div>
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="info@business.com"
          className="mt-1 w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
        <div className="flex items-center gap-2 mt-1.5">
          <button
            type="button"
            onClick={() => setShowEmailOnWebsite(!showEmailOnWebsite)}
            className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
              showEmailOnWebsite ? 'bg-green-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
                showEmailOnWebsite ? 'translate-x-3.5' : 'translate-x-0.5'
              }`}
            />
          </button>
          <span className="text-[11px] text-gray-500">
            {showEmailOnWebsite ? 'Email visible on website' : 'Email hidden from website'}
          </span>
        </div>
      </div>

      {/* Hours */}
      <div className="pt-2 border-t border-gray-100">
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Hours
        </label>
        <HoursEditor hours={hours} onChange={setHours} />
      </div>

      {/* Social Links */}
      <div className="pt-2 border-t border-gray-100">
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">
          Social Links
        </label>
        <div className="space-y-2">
          <SocialInput label="Facebook" value={facebook} onChange={setFacebook} placeholder="https://facebook.com/..." />
          <SocialInput label="Instagram" value={instagram} onChange={setInstagram} placeholder="https://instagram.com/..." />
          <SocialInput label="LinkedIn" value={linkedin} onChange={setLinkedin} placeholder="https://linkedin.com/in/..." />
          <SocialInput label="YouTube" value={youtube} onChange={setYoutube} placeholder="https://youtube.com/..." />
          <SocialInput label="Yelp" value={yelp} onChange={setYelp} placeholder="https://yelp.com/biz/..." />
          <SocialInput label="Google" value={google} onChange={setGoogle} placeholder="https://g.page/..." />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

// =============================================================================
// HOURS EDITOR
// =============================================================================

type HoursData = Record<string, { open: string; close: string; closed?: boolean }>;

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_LABELS: Record<string, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
  thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

const DEFAULT_HOURS: HoursData = {
  monday: { open: '09:00', close: '17:00' },
  tuesday: { open: '09:00', close: '17:00' },
  wednesday: { open: '09:00', close: '17:00' },
  thursday: { open: '09:00', close: '17:00' },
  friday: { open: '09:00', close: '17:00' },
  saturday: { open: '10:00', close: '18:00' },
  sunday: { open: 'closed', close: 'closed', closed: true },
};

function HoursEditor({
  hours,
  onChange,
}: {
  hours: HoursData;
  onChange: (h: HoursData) => void;
}) {
  const updateDay = (day: string, field: 'open' | 'close' | 'closed', value: string | boolean) => {
    const updated = { ...hours };
    const dayData = { ...updated[day] };

    if (field === 'closed') {
      if (value) {
        dayData.open = 'closed';
        dayData.close = 'closed';
        dayData.closed = true;
      } else {
        dayData.open = '09:00';
        dayData.close = '17:00';
        dayData.closed = false;
      }
    } else {
      dayData[field] = value as string;
    }

    updated[day] = dayData;
    onChange(updated);
  };

  const applyMondayToWeekdays = () => {
    const mon = hours.monday;
    const updated = { ...hours };
    for (const day of ['tuesday', 'wednesday', 'thursday', 'friday']) {
      updated[day] = { ...mon };
    }
    onChange(updated);
  };

  const isClosed = (day: string) => hours[day]?.closed || hours[day]?.open === 'closed';

  return (
    <div className="space-y-1.5">
      {DAY_ORDER.map((day) => (
        <div key={day} className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 w-8 shrink-0">{DAY_LABELS[day]}</span>
          <button
            type="button"
            onClick={() => updateDay(day, 'closed', !isClosed(day))}
            className={`px-1.5 py-0.5 text-[10px] rounded border transition-colors ${
              isClosed(day)
                ? 'bg-gray-100 text-gray-500 border-gray-200'
                : 'bg-green-50 text-green-700 border-green-200'
            }`}
          >
            {isClosed(day) ? 'Closed' : 'Open'}
          </button>
          {!isClosed(day) && (
            <>
              <input
                type="time"
                value={hours[day]?.open || '09:00'}
                onChange={(e) => updateDay(day, 'open', e.target.value)}
                className="px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-[90px]"
              />
              <span className="text-xs text-gray-400">-</span>
              <input
                type="time"
                value={hours[day]?.close || '17:00'}
                onChange={(e) => updateDay(day, 'close', e.target.value)}
                className="px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-[90px]"
              />
            </>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={applyMondayToWeekdays}
        className="text-[10px] text-blue-600 hover:text-blue-800 mt-1"
      >
        Apply Mon hours to Tue–Fri
      </button>
    </div>
  );
}

/**
 * Format hours data for compact read-mode display.
 * Groups consecutive days with identical hours.
 */
function formatHoursCompact(hours: HoursData): string {
  const groups: Array<{ days: string[]; open: string; close: string }> = [];

  for (const day of DAY_ORDER) {
    const h = hours[day];
    if (!h) continue;
    const open = h.closed || h.open === 'closed' ? 'closed' : h.open;
    const close = h.closed || h.close === 'closed' ? 'closed' : h.close;
    const last = groups[groups.length - 1];
    if (last && last.open === open && last.close === close) {
      last.days.push(DAY_LABELS[day]);
    } else {
      groups.push({ days: [DAY_LABELS[day]], open, close });
    }
  }

  return groups.map((g) => {
    const label = g.days.length > 1
      ? `${g.days[0]}–${g.days[g.days.length - 1]}`
      : g.days[0];
    if (g.open === 'closed') return `${label}: Closed`;
    // Format time from HH:MM to display
    const fmt = (t: string) => {
      const [hh, mm] = t.split(':').map(Number);
      const period = hh >= 12 ? 'pm' : 'am';
      const hour = hh > 12 ? hh - 12 : hh === 0 ? 12 : hh;
      return `${hour}${mm > 0 ? ':' + mm.toString().padStart(2, '0') : ''}${period}`;
    };
    return `${label}: ${fmt(g.open)}–${fmt(g.close)}`;
  }).join(', ');
}

function SocialDisplay({ label, url }: { label: string; url: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 w-16">{label}</span>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-blue-600 hover:underline truncate"
      >
        {url.replace(/^https?:\/\/(www\.)?/, '')}
      </a>
    </div>
  );
}

function SocialInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 w-16 shrink-0">{label}</span>
      <input
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
      />
    </div>
  );
}

// =============================================================================
// LOGO EDITOR
// =============================================================================

interface LogoEditorProps {
  websiteId: string;
  hasLogo: boolean;
  logoUrl: string | null;
  logoHeightClass?: string;
  logoHeightOptions?: readonly string[];
  onLogoChanged: (newUrl: string | null) => void;
  onLogoHeightChanged: (heightClass: string) => void;
}

/** Human labels for the Tailwind height classes — staff shouldn't read CSS. */
const LOGO_SIZE_LABELS: Record<string, string> = {
  'h-8': 'XS',
  'h-10': 'S',
  'h-12': 'M',
  'h-14': 'L',
  'h-16': 'XL',
};

function LogoEditor({
  websiteId,
  hasLogo,
  logoUrl,
  logoHeightClass,
  logoHeightOptions,
  onLogoChanged,
  onLogoHeightChanged,
}: LogoEditorProps) {
  const [savingHeight, setSavingHeight] = useState<string | null>(null);
  const sizeOptions = logoHeightOptions && logoHeightOptions.length > 0
    ? logoHeightOptions
    : ['h-8', 'h-10', 'h-12', 'h-14', 'h-16'];
  const activeHeight = logoHeightClass || 'h-10';

  const handleHeightChange = useCallback(async (heightClass: string) => {
    if (heightClass === activeHeight) return;
    setSavingHeight(heightClass);
    try {
      await updateBusinessInfo(websiteId, { logoHeightClass: heightClass });
      onLogoHeightChanged(heightClass);
    } catch (err) {
      console.error('Failed to update logo size:', err);
    } finally {
      setSavingHeight(null);
    }
  }, [websiteId, activeHeight, onLogoHeightChanged]);

  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cropper state
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  // The file as chosen, kept so "Skip Crop" can upload it untouched. Null when
  // re-cropping the stored logo: that file is already cropped, so skipping it
  // would upload the same cut-off image again.
  const [tempFile, setTempFile] = useState<File | null>(null);

  // Upload the final file (after crop or directly for SVG)
  const doUpload = useCallback(async (file: File) => {
    setUploading(true);
    setLogoError(null);

    try {
      const result = await uploadLogo(websiteId, file);
      onLogoChanged(result.logoUrl || null);
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [websiteId, onLogoChanged]);

  // File selection: validate, then show cropper (or upload directly for SVG)
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setLogoError('Invalid file type. Use PNG, JPG, SVG, or WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setLogoError('File too large. Maximum 5MB.');
      return;
    }

    setLogoError(null);

    // SVG files skip cropper (they scale infinitely)
    if (file.type === 'image/svg+xml') {
      doUpload(file);
    } else {
      // Show cropper for raster images
      const tempUrl = URL.createObjectURL(file);
      setTempImageUrl(tempUrl);
      setTempFile(file);
      setShowCropper(true);
    }

    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [doUpload]);

  // Cropper complete: convert Blob → File and upload
  const handleCropComplete = useCallback(async (croppedBlob: Blob) => {
    setShowCropper(false);
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl);
      setTempImageUrl(null);
    }

    setTempFile(null);
    const croppedFile = new File([croppedBlob], 'logo.png', { type: 'image/png' });
    await doUpload(croppedFile);
  }, [tempImageUrl, doUpload]);

  // Cropper cancel
  const handleCropCancel = useCallback(() => {
    setShowCropper(false);
    setTempFile(null);
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl);
      setTempImageUrl(null);
    }
  }, [tempImageUrl]);

  // Skip Crop: upload the chosen file as it is, as the onboarding wizard does.
  const handleCropSkip = useCallback(async () => {
    const file = tempFile;
    setShowCropper(false);
    setTempFile(null);
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl);
      setTempImageUrl(null);
    }
    if (file) await doUpload(file);
  }, [tempFile, tempImageUrl, doUpload]);

  // Re-crop an existing logo
  const handleRecrop = useCallback(() => {
    if (!logoUrl) return;
    setTempFile(null);
    setTempImageUrl(logoUrl);
    setShowCropper(true);
  }, [logoUrl]);

  const handleRemove = useCallback(async () => {
    setRemoving(true);
    setLogoError(null);

    try {
      await removeLogo(websiteId);
      onLogoChanged(null);
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      setRemoving(false);
    }
  }, [websiteId, onLogoChanged]);

  return (
    <div>
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Logo</label>

      {logoError && (
        <p className="text-xs text-red-600 mt-1">{logoError}</p>
      )}

      {hasLogo && logoUrl ? (
        /* Logo exists — show preview + change/recrop/remove buttons */
        <div className="mt-1.5 space-y-2">
          <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg border border-gray-200">
            <img
              src={logoUrl}
              alt="Business logo"
              className="max-h-10 object-contain"
            />
          </div>

          {/* Nav logo size — rebuilds the scaffold on every site page */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                Size in nav
              </span>
              {savingHeight && (
                <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
              )}
            </div>
            <div className="flex gap-1">
              {sizeOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => handleHeightChange(opt)}
                  disabled={!!savingHeight}
                  title={`Logo height ${opt.replace('h-', '')} (${opt})`}
                  className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-colors disabled:opacity-50 ${
                    opt === activeHeight
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {LOGO_SIZE_LABELS[opt] || opt}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              Applies to every page. Trim empty space around the logo file for the biggest effect.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || removing}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors disabled:opacity-50"
            >
              {uploading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Upload className="w-3 h-3" />
              )}
              {uploading ? 'Uploading...' : 'Change'}
            </button>
            <button
              onClick={handleRecrop}
              disabled={uploading || removing}
              className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
              title="Adjust crop"
            >
              <Image className="w-3 h-3" />
              Crop
            </button>
            <button
              onClick={handleRemove}
              disabled={uploading || removing}
              className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded transition-colors disabled:opacity-50"
            >
              {removing ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Trash2 className="w-3 h-3" />
              )}
              {removing ? '...' : 'Remove'}
            </button>
          </div>
        </div>
      ) : (
        /* No logo — show upload button */
        <div className="mt-1.5">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full flex items-center justify-center gap-2 px-3 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 transition-colors disabled:opacity-50"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-xs font-medium">Uploading...</span>
              </>
            ) : (
              <>
                <Image className="w-4 h-4" />
                <span className="text-xs font-medium">Upload Logo</span>
              </>
            )}
          </button>
          <p className="text-[10px] text-gray-400 mt-1 text-center">
            PNG, JPG, SVG, or WebP · Max 5MB
          </p>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.svg,.webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Cropper modal */}
      {showCropper && tempImageUrl && (
        <LogoCropper
          imageUrl={tempImageUrl}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          onSkip={tempFile ? handleCropSkip : undefined}
        />
      )}
    </div>
  );
}

// =============================================================================
// COLLAPSIBLE SECTION HELPER
// =============================================================================

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  badge?: string;
}

function CollapsibleSection({
  title,
  icon,
  isExpanded,
  onToggle,
  children,
  badge,
}: CollapsibleSectionProps) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium text-sm text-gray-900">{title}</span>
          {badge && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium text-blue-600 bg-blue-50 rounded">
              {badge}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500" />
        )}
      </button>
      {isExpanded && <div className="px-3 py-3 bg-white">{children}</div>}
    </div>
  );
}
